<script lang="ts">
  import PlaceholderImage from "./PlaceholderImage.svelte";
  import Skeleton from "svelte-skeleton/Skeleton.svelte";

  import type { ImageObj } from "@shared/types/Images";

  import { isAwsS3Image } from "@shared/svelte/Modules/AwsS3Service";

  export let className = "";
  export let forceShowPlaceholder = null;
  export let product: ImageObj;
  export let width = null;
  export let height = null;
  export let setImageContainerHeight: boolean = false;
  export let onClick = null;
  export let extraAttrs = {};
  export let fizz: boolean = false;
  export let aspectRatio: string = "100";
  export let eTrimMode: boolean = true;
  export let isContentCard: boolean = false;
  export let usePlaceholderImage: boolean = false;
  export let auto = false;
  export let maxHeight = null;
  export let skeleton = false;

  let productImageEndpoint = isContentCard ? "content-cards" : "product-lines";

  $: imageContainerHeight = 50;

  let productImageClass = !width && !height ? "" : "productImage";
  $: usePlaceholder = false;

  let loaded = false;
  //@ts-ignore
  const constants = window.C;

  const productTypePlaceholders = {
    null: constants.IMAGE_PATHS["product_type_beer.png"],
    "1": constants.IMAGE_PATHS["product_type_beer.png"],
    "2": constants.IMAGE_PATHS["product_type_wine.png"],
    "3": constants.IMAGE_PATHS["product_type_liquor.png"],
    "4": constants.IMAGE_PATHS["product_type_other.png"],
  };

  function stagingAdjustment(p, imageBase) {
    if (height == "55") {
      return `${imageBase}/${productImageEndpoint}-small/${
        p.cloudinary_image_url.split("/").slice(-1)[0]
      }`;
    } else if (height == "300") {
      return `${imageBase}/${productImageEndpoint}-large/${
        p.cloudinary_image_url.split("/").slice(-1)[0]
      }`;
    } else {
      return `${imageBase}/${productImageEndpoint}/${
        p.cloudinary_image_url.split("/").slice(-1)[0]
      }`;
    }
  }

  function prepUrl(p) {
    if (p) {
      usePlaceholder = false;
      const host = process.env.CLOUDINARY_HOST || "res.cloudinary.com";
      const bucket = process.env.CLOUDINARY_BUCKET || "tiz2";

      let base = `//${host}/${bucket}/image/upload/`;
      //e_trim crops image based on color similarity. Tolerance is 0-100 with 10 being the default
      //when we do not want to trim images (i.e. storefront featured product non-provi images), let's specify
      if (eTrimMode) {
        base = base + "e_trim/";
      }

      let base2: string;
      if (width && height) {
        base2 = `${base}f_auto,w_${width},h_${height},c_pad,b_white`;
      } else {
        base2 = `${base}f_auto,c_fit`;
      }

      if (p.cloudinary_image_url) {
        if (isAwsS3Image(p.cloudinary_image_url)) {
          return p.cloudinary_image_url;
        } else if (
          bucket === "dlamvouvc" &&
          p.cloudinary_image_url.split(productImageEndpoint).length > 1
        ) {
          return stagingAdjustment(p, base2);
        }
        return `${base2}${p.cloudinary_image_url}`;
      } else if (p.image_name) {
        return `${base2}/v${p.image_version}/${productImageEndpoint}/${p.image_name}`;
      } else {
        usePlaceholder = true;
        return (
          productTypePlaceholders[p.product_type_id] ||
          constants.IMAGE_PATHS["icons/photo-placeholder.svg"]
        );
      }
    } else {
      usePlaceholder = true;
      return constants.IMAGE_PATHS["icons/photo-placeholder.svg"];
    }
  }
  $: url = prepUrl(product);

  function handleClick(event) {
    if (onClick) {
      onClick(event);
    }
  }

  const onImgLoad = () => {
    loaded = true;
  };

  const setContainerHeight = event => {
    if (((event || {}).srcElement || {}).naturalHeight) {
      imageContainerHeight = event.srcElement.naturalHeight;
    }
  };

</script>

<style>
  .product-line-image {
    max-height: 175px;
  }

</style>

{#if forceShowPlaceholder}
  <PlaceholderImage {height} {width} />
{/if}

{#if product}
  {#if fizz}
    {#if usePlaceholder && !usePlaceholderImage}
      <div
        class={`product-image-class fizz-placeholder ${productImageClass} ${className}`}
        style="width: 100%; height: 0; padding-bottom: {aspectRatio}%"
      />
    {:else if setImageContainerHeight}
      <div
        {...extraAttrs}
        class="product-image-class fizz-image-container"
        style={`--height: ${imageContainerHeight}px`}
      >
        <img
          on:load={setContainerHeight}
          src={url}
          class={`${className ? className : ""}`}
          alt="hero"
        />
      </div>
    {:else if auto}
      <img
        src={url}
        class={`product-image-class ${className != "" ? className : "fizz-margin-auto"}`}
        alt="product"
        {...extraAttrs}
        style="max-height: 9.375rem;"
      />
    {:else if width && height}
      <img
        on:load={onImgLoad}
        src={url}
        class={`product-image-class ${className !== "" ? className : "fizz-margin-auto"}`}
        alt="product"
        style={`width: ${width}px; min-width: ${width}px; height: ${height}px; min-height: ${height}px; display: ${
          !loaded && skeleton ? "none" : "initial"
        }`}
        {...extraAttrs}
      />
      {#if skeleton && !loaded}
        <Skeleton animate={true} {width} {height} ariaLabel="product-image-loading" />
      {/if}
    {:else}
      <img
        src={url}
        class={`product-image-class ${className !== "" ? className : "fizz-margin-auto"}`}
        alt="product"
        style={maxHeight ? `max-height: ${maxHeight}; width: auto;` : ``}
        {...extraAttrs}
      />
    {/if}
  {:else}
    <div
      {...extraAttrs}
      on:click={handleClick}
      class={`product-image-class ${productImageClass} ${className}`}
      style="background-image: url({url})"
    />
  {/if}
{/if}
